import React, { useRef } from 'react';
import { findDOMNode } from 'react-dom';
import classnames from 'classnames';
import { dfs } from '../../utils';
import { Modal, Button, Select, Input, ButtonGroup, Tooltip } from '@platform/base';
import { getSysFieldid } from '@platform/api';
import './index.less';

const DEFAULT_INFO = { logic: 'or', conditions: [] }; //conditions:[{key, value, logic}]

function filterItem(rowKey, condition, getCellValue, getCellProps){
    let { key, value = '', logic } = condition;
    if(!key || key === SELECT_KEY_EMPTY) return false;

    let cellValue = getCellValue(rowKey, key);
    let display = String(cellValue._display_ || cellValue.display || cellValue.value || '').toLowerCase();
    value = value.toLowerCase();
    let itemType = getCellProps(rowKey, key, 'itemtype');
    switch(logic){
        case 'equal':
            return itemType === 'number' && !isNaN(display) && !isNaN(value)
                ? Number(display) === Number(value)
                : display === value;
        case 'greaterThan':
            return itemType === 'number' && !isNaN(display) && !isNaN(value) && Number(display) > Number(value);
        case 'lessThan':
            return itemType === 'number' && !isNaN(display) && !isNaN(value) && Number(display) < Number(value);
        case 'notIncluded':
            return value ? !display.includes(value) : display !== value;
        case 'included':
            return value ? display.includes(value) : display === value;
    }
}

function filterRows(rowKeys, filterInfo, getCellValue, getCellProps){
    if(!filterInfo || !filterInfo.conditions || !filterInfo.conditions.length) return rowKeys;

    return rowKeys.filter(rowKey => {
        switch(filterInfo.logic){
            case 'and':
                return filterInfo.conditions.every(condition => {
                    return filterItem(rowKey, condition, getCellValue, getCellProps);
                });
            case 'or':
            default:
                return filterInfo.conditions.some(condition => {
                    return filterItem(rowKey, condition, getCellValue, getCellProps);
                });
        }
    });
}

const { Header, Body, Footer, Title } = Modal;
const SELECT_KEY_EMPTY = '0';
const DEFAULT_ITEM_LOGIC = 'included';

export default function useFilter(Table){
    return class FilterTable extends React.Component {

        static defaultProps = { filterConfig: { } };

        state = {
            showModal: false,
            items: [],
            logic: 'or',
            showTip: false,
        };

        savedItems = [];
        lastActiveElement = null; // 最后一次聚焦的元素，用于恢复焦点

        render(){
            let { filterConfig, columns = [], locale } = this.props;
            let { items, logic, showModal, showTip } = this.state;

            const { enable, title, onReset } = filterConfig;

            let options = [{ key: SELECT_KEY_EMPTY, label: locale['table0026' /*请选择*/] }];
            let filteredKeys = new Set(this.savedItems.map(item => item.key));

            if(enable){
                columns = dfs(columns, col => {
                    if(!col.children && col.filterable) { 
                        options.push({ key: col.key, label: col.label });
                        return {
                            ...col,
                            title: (
                                <>
                                    {col.title}
                                    <Tooltip
                                        placement="top"
                                        overlay={locale['table0028']}
                                        delayShow={300}
                                    >
                                        <i className={classnames("header-icon", { 'fixed-icon': filteredKeys.has(col.key) })}>
                                            <i 
                                                className={
                                                    filteredKeys.has(col.key) ? "iconfont icon-lieguolv" : "iconfont icon-sxuan"
                                                } 
                                                onClick={() => {
                                                    if(filteredKeys.has(col.key)){
                                                        // 存在筛选条件, 删除这个列的所有条件
                                                        this.deleteItemByKey(col.key);
                                                    } else {
                                                        this.addItem({ key: col.key, value: '', logic: DEFAULT_ITEM_LOGIC });
                                                        this.setState({ showModal: true, showTip: false });
                                                    }
                                                }}
                                            />
                                        </i>
                                    </Tooltip>
                                </>
                            ),
                        };
                    }
                    return col;
                });
            }

            return (
                enable ? (
                    <React.Fragment>
                        <Table {...this.props} columns={columns}/>
                        <Modal 
                            className="filter-modal" 
                            show={showModal} 
                            onHide={() => {
                                this.recoverItems();
                                this.closeModal();
                            }}
                            autoFocus={false}
                            modalAutoFocus={false} // base-modal里的属性
                            ref={dom => {
                                this.modalDOM = findDOMNode(dom);
                            }}
                            fieldid="filterPanel"
                            zIndex={1000}
                        >
                            <Header closeButton>
                                <Title 
                                    fieldid={getSysFieldid(`${locale['table0028' /*筛选*/]}${title || ''}`)}
                                >
                                    {`${locale['table0028' /*筛选*/]}${title || ''}:`}
                                    <span className={classnames(showTip ? 'table-filter-tips' : 'filter-tips-hidden')}>
                                        <span className="iconfont icon icon-gantanhao"/>
                                        {showTip ? locale['table0027' /*没有找到，换个词试一试*/] : ''}
                                    </span>
                                </Title>
                            </Header>    
                            <Body>
                                <div className="condition-logic-container">
                                    {locale['filter-under-condition'] /*以下条件*/}
                                    <Select
                                        value={logic}
                                        onChange={value => {
                                            this.setState({ logic: value });
                                            if (this.lastActiveElement) {
                                                this.lastActiveElement.focus();
                                            }
                                        }}
                                        onBlur={() => {
                                            // change或者直接关闭都需要清空
                                            this.lastActiveElement = null;
                                        }}
                                        onMouseDown={() => {
                                            if (!this.lastActiveElement) {
                                                this.lastActiveElement = document.activeElement;
                                            }
                                        }}
                                        showClear={false}
                                    >
                                        <Select.Option key='and'>{locale['filter-all-condition'] /*全部*/}</Select.Option>
                                        <Select.Option key='or'>{locale['filter-any-condition'] /*任一*/}</Select.Option>
                                    </Select>
                                    {locale['filter-accord'] /*符合*/}
                                </div>
                                {
                                    items.map((item, index) => {
                                        return <FilterItem 
                                            key={index}
                                            item={item}
                                            options={options}
                                            onDelete={() => this.deleteItemByIndex(index)}
                                            disabled={item.key === SELECT_KEY_EMPTY}
                                            onChange={item => {
                                                items[index] = item;
                                                this.setState({ items });
                                            }}
                                            locale={locale}
                                            onKeyDown={e => {
                                                e.which === 13 && this.handleFilterClick();
                                            }}
                                        />;
                                    })
                                }
                                <div className="filter-item">
                                    <div 
                                        className="add" 
                                        onClick={() => {
                                            this.addItem({ key: SELECT_KEY_EMPTY, value: '', logic: DEFAULT_ITEM_LOGIC });
                                            requestAnimationFrame(() => {
                                                this.modalDOM.querySelector('.u-modal-body').scrollTop = 999999;
                                            });
                                        }}
                                        fieldid={getSysFieldid('add_btn')}
                                    >
                                        {`+${locale['table0029']}` /*添加条件*/}
                                    </div>
                                    <Button 
                                        // style={{ flex: 1 }}
                                        onClick={() => this.setState({
                                            items: [
                                                {
                                                    key: SELECT_KEY_EMPTY, 
                                                    value: '', 
                                                    logic: DEFAULT_ITEM_LOGIC,
                                                },
                                            ], 
                                        }, () => {
                                            this.modalDOM.querySelector('.modal-item input.u-select-selection-search-input')?.focus();
                                        })}
                                        fieldid={getSysFieldid("clear")}
                                    >
                                        {locale['table0030'] /*清空*/}
                                    </Button>
                                    
                                </div>
                            </Body>
                            <Footer>
                                <ButtonGroup>
                                    <Button 
                                        colors="primary" 
                                        onClick={this.handleFilterClick}
                                        className="button-primary"
                                        fieldid={getSysFieldid("filter")}
                                    >
                                        {locale['table0028'] /*筛选*/}
                                    </Button>
                                    <Button 
                                        onClick={() => {
                                            this.setState({ items: [], logic: 'or' });
                                            this.savedItems = [];
                                            onReset?.();
                                            this.closeModal();
                                        }}
                                        fieldid={getSysFieldid("reset")}
                                    >
                                        {locale['table0031'] /*重置*/}
                                    </Button>
                                </ButtonGroup>
                                <Button 
                                    onClick={() => {
                                        this.recoverItems();
                                        this.closeModal();
                                    }}
                                    fieldid={getSysFieldid("cancel")}
                                >
                                    {locale['table_cancel'] /*取消*/}
                                </Button>
                            </Footer>
                        </Modal>
                    </React.Fragment>
                ) : <Table {...this.props} />
            );
        }

        componentDidUpdate(prevProps) {
            let prevCondition = prevProps?.filterConfig?.conditions ?? [],
                condition = this.props?.filterConfig?.conditions || [];
                
            if (JSON.stringify(prevCondition) !== JSON.stringify(condition)) {
                this.savedItems = condition;
                this.setState({ items: [...condition], showTip: false });
            }
        }

        componentDidMount() {
            document.addEventListener('keydown', this.handleKeyDown);
        }

        componentWillUnmount() {
            document.removeEventListener('keydown', this.handleKeyDown);
        }

        handleKeyDown = e => {
            if(!e.ctrlKey || e.keyCode != 70) return; // ctrl + F

            let { store } = this.props;
            if(!store.getSelectedCell()?.size) return; // 焦点不在表格内

            this.addItem({ key: SELECT_KEY_EMPTY, value: '', logic: DEFAULT_ITEM_LOGIC });
            this.setState({ showModal: true, showTip: false });
            e.preventDefault();
        }

        handleFilterClick = () => {
            let { items, logic } = this.state,
                { onFilter } = this.props.filterConfig || {};

            items = items.filter(item => item.key !== SELECT_KEY_EMPTY);

            if(!items.length) {
                items = [{ key: SELECT_KEY_EMPTY, value: '', logic: DEFAULT_ITEM_LOGIC }];
            }
            onFilter(items, logic).then(rows => {
                if(rows.length){
                    // 保存之前的条件
                    this.saveItems(items);
                    this.closeModal();
                }
                this.setState({ items, showTip: !rows.length });
            });
        }

        closeModal = () => {
            this.setState({ showModal: false });
        }

        addItem = item => {
            const { items } = this.state;
            items.push(item);
            this.setState({ items });
        }

        deleteItemByKey = key => {
            let { items, logic } = this.state;
            const { onFilter } = this.props.filterConfig || {};

            items = items.filter(item => item.key !== key);
            onFilter?.(items, logic);

            this.saveItems(items);

            this.setState({ items });
        }

        deleteItemByIndex = index => {
            let { items } = this.state;
            items.splice(index, 1);
            this.setState({ items });
        }

        saveItems = items => {
            // 保存之前的条件
            this.savedItems = JSON.parse(JSON.stringify(items));
        }

        recoverItems = () => {
            this.setState({ items: JSON.parse(JSON.stringify(this.savedItems)) });
        }
    };
}

function FilterItem(props){
    const { options, item = {}, onChange, onDelete, disabled = false, locale, onKeyDown } = props;
    const inputComponent = useRef(null);
    const filterModal = useRef(null);
    let lastActiveElement;

    function handleSelectChange(value){
        onChange?.({ key: value, value: item.value, logic: item.logic });
        setTimeout(() => { // 延迟，确保输入框已解除disabled
            findDOMNode(inputComponent.current).querySelector('input').focus();
        });
    }

    function handleInputChange(value){
        onChange?.({ key:item.key, value, logic:item.logic });
    }

    function handleLogicChange(value){
        onChange?.({ key:item.key, value:item.value, logic:value });
        setTimeout(() => { // 延迟，确保输入框已解除disabled
            findDOMNode(inputComponent.current).querySelector('input').focus();
        });
    }

    function recordLastActiveElement() {
        if (!lastActiveElement) {
            lastActiveElement = document.activeElement;
        }
    }

    function focusLastActiveElement() {
        // Select blur 触发此函数
        // 未做选择关闭弹窗，需要复原焦点，这里不用考虑onChange触发，因为那边有延迟
        lastActiveElement && lastActiveElement.focus();
        lastActiveElement = null;
    }

    return (
        <div className="modal-item" ref={filterModal}>
            <Select value={item.key} onChange={handleSelectChange} showClear={false} autoFocus={disabled}
                onBlur={focusLastActiveElement}
                onMouseDown={recordLastActiveElement}
                getPopupContainer={() => filterModal.current}
                onKeyDown={onKeyDown}
            >
                {
                    options.map(option => (<Select.Option key={option.key}>{option.label}</Select.Option>))
                }
            </Select>
            <Select value={item.logic} onChange={handleLogicChange} showClear={false}
                onBlur={focusLastActiveElement}
                onMouseDown={recordLastActiveElement}
                onKeyDown={onKeyDown}
            >
                <Select.Option key='equal'>{locale['filter-equal'] /*等于*/}</Select.Option>
                <Select.Option key='greaterThan'>{locale['filter-greater-than'] /*大于*/}</Select.Option>
                <Select.Option key='lessThan'>{locale['filter-less-than'] /*小于*/}</Select.Option>
                <Select.Option key='included'>{locale['filter-included'] /*包含*/}</Select.Option>
                <Select.Option key='notIncluded'>{locale['filter-not-included'] /*不包含*/}</Select.Option>
            </Select>
            <Input 
                ref={inputComponent} 
                value={item.value} 
                onChange={handleInputChange} 
                autoFocus={!disabled} 
                disabled={disabled}
                onKeyDown={onKeyDown}
            />
            <i className="iconfont icon-shanchu" onClick={onDelete}/>
        </div>

    );
}

useFilter.displayName = 'useFilter';
useFilter.dependence = [];
useFilter.hooks = function(){
    this._filter = rowKeys => {
        return filterRows(rowKeys, this.store.filterInfo, this.getCellValue, this.getCellProps);
    };
    return {
        getFilterInfo: () => { 
            return this.store.filterInfo || DEFAULT_INFO;
        },
        setFilterInfo: info => {
            this.store.filterInfo = info || DEFAULT_INFO;
            this._updateVisibleRows();
            this._updateTotal();
            this.forceUpdate();
        },
    };
};
useFilter.propsGenerator = function(props){
    if(!props.filterConfig?.enable){
        return props;
    }

    let { store, filterConfig } = props;
    let info = store.getFilterInfo();

    return {
        ...props,
        filterConfig: {
            ...filterConfig,
            ...info,
            onFilter: (conditions, logic) => {
                return new Promise(resolve => {
                    let rowKeys = store.getRowKeys();
                    let info = store.getFilterInfo();
    
                    let newInfo = { ...info, conditions: [...conditions], logic };
                    let filterRowKeys = filterRows(rowKeys, newInfo, store.getCellValue, store.getCellProps);
                    if(filterRowKeys.length){
                        store.setFilterInfo(newInfo);
                    }
    
                    filterConfig?.onFilter?.();
                    resolve(filterRowKeys);
                });
            },
            onClear: () => {
                store.setFilterInfo(DEFAULT_INFO);
                filterConfig?.onClear?.();
            },
            onReset: () => {
                store.setFilterInfo(DEFAULT_INFO);
                filterConfig?.onReset?.();
            },
        },
    };
};